import { useState, useEffect, useCallback } from 'react';
import { SmartSearchEngine } from '../utils/smartSearchEngine';
import { getApiBaseUrl } from '../utils/apiUrl';

/**
 * Custom hook for Pokemon Search Index
 * Pre-loads and manages Pokemon search index
 */
export function usePokemonSearchIndex() {
  const [searchEngine, setSearchEngine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load Pokemon data and build index
  useEffect(() => {
    let isMounted = true;
    
    const loadIndex = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch Pokemon data from backend
        // Use pokemon_name_mappings for better search coverage
        const response = await fetch(
          `${getApiBaseUrl()}/api/pokemon?limit=10000&page=1`
        );
        
        if (!response.ok) {
          throw new Error('Failed to load Pokemon data');
        }
        
        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to load Pokemon data');
        }
        
        const pokemonList = data.data || [];
        
        // Also try to fetch from mapping API for additional search data
        try {
          const mappingResponse = await fetch(
            `${getApiBaseUrl()}/api/mapping/pokemon/all`
          );
          
          if (mappingResponse.ok) {
            const mappingData = await mappingResponse.json();
            if (mappingData.success && mappingData.data) {
              // Ensure data is an array
              const mappingArray = Array.isArray(mappingData.data) 
                ? mappingData.data 
                : (mappingData.data.data || []);
              
              if (!Array.isArray(mappingArray)) {
                console.warn('[SEARCH INDEX] Mapping data is not an array:', typeof mappingData.data, mappingData);
                throw new Error('Mapping data must be an array');
              }
              
              // Merge mapping data with Pokemon data
              const mappingMap = new Map();
              mappingArray.forEach(mapping => {
                const id = mapping.pokemon_id || mapping.id;
                if (id) {
                  mappingMap.set(id, mapping);
                }
              });
              
              // Enrich Pokemon data with mapping info
              pokemonList.forEach(pokemon => {
                const id = pokemon.id || pokemon.Pokemon_ID;
                const mapping = mappingMap.get(id);
                if (mapping) {
                  Object.assign(pokemon, {
                    name_english: mapping.name_english || pokemon.Pokemon_Name_EN,
                    name_thai: mapping.name_thai || pokemon.Pokemon_Name_TH,
                    name_species_id: mapping.name_species_id
                  });
                }
              });
            }
          }
        } catch (mappingError) {
          console.warn('Could not load mapping data:', mappingError);
          // Continue without mapping data
        }
        
        if (!isMounted) return;
        
        // Build search engine
        const engine = new SmartSearchEngine(pokemonList);
        setSearchEngine(engine);
        
        console.log(`✅ Pokemon Search Index loaded: ${pokemonList.length} Pokemon`);
      } catch (err) {
        console.error('Error loading Pokemon search index:', err);
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadIndex();
    
    return () => {
      isMounted = false;
    };
  }, []);
  
  const search = useCallback((query, options = {}) => {
    if (!searchEngine) {
      return [];
    }
    
    return searchEngine.search(query, options);
  }, [searchEngine]);
  
  const refresh = useCallback(async () => {
    // Force reload index
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/pokemon?limit=10000&page=1`
      );
      
      if (!response.ok) {
        throw new Error('Failed to refresh Pokemon data');
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to refresh Pokemon data');
      }
      
      const pokemonList = data.data || [];
      const engine = new SmartSearchEngine(pokemonList);
      setSearchEngine(engine);
    } catch (err) {
      console.error('Error refreshing Pokemon search index:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  return {
    searchEngine,
    search,
    loading,
    error,
    refresh
  };
}
